import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { EventType } from '@lib/models/enums';
import { InteractionPartUtils } from '@lib/models/InteractionPart';
import type { PartRendererProps } from './types';
import { useAiChatServices } from '../../AiChatContext';
import { useTheme } from '@mui/material/styles';

/**
 * Renders an `ask_user` tool call — the built-in default questionnaire component (the fallback the
 * AskUserDispatcher draws when a call's `[KernelUIComponent]` name isn't host-registered). Two faces:
 *  - INTERACTIVE (unanswered) — the questionnaire form, shown live in the pane-level AskUserSheet.
 *    Each question renders a control by its `kind` (mirrors the server's `AskQuestion.Type`):
 *    `single` => buttons, `multi` => checkboxes, `dropdown` => a Select, `date` => a DatePicker,
 *    `masked` => a masked text field, `text` => free text. Choice kinds also get an optional note
 *    (doubles as the "none of the above" escape). One single-select question with no note submits on a
 *    single click; touch the note and a Submit button appears.
 *  - LOCKED (answered / reload) — a pure Question -> Answer summary (no
 *    controls), rendered inline in the transcript.
 *
 * Submitting sends the answers back as a JSON string over the WebSocket
 * (ChatAction.AnswerHumanTool, keyed by the tool-call id), unblocking the parked
 * server-side tool so the turn continues.
 */

type SelectMode = 'single' | 'multi';
/** The component drawn for a question. Mirrors the server's `AskQuestion.Type`. */
type QKind = 'single' | 'multi' | 'dropdown' | 'date' | 'masked' | 'text';
const CHOICE_KINDS: QKind[] = ['single', 'multi', 'dropdown'];
interface AskQuestion {
    id?: string;
    text: string;
    select: SelectMode;
    options: string[];
    kind: QKind;
    /** Input mask for a `masked` question, e.g. "(00) 00000-0000". */
    mask?: string;
}
interface Questionnaire {
    title?: string;
    questions: AskQuestion[];
}
interface QAnswer {
    questionId: string;
    selected: string[];
    note?: string;
}
interface QState {
    selected: string[];
    note: string;
}

const asObj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');

/** ISO shape the `date` questions round-trip in (`selected: ["2026-08-14"]`). */
const DATE_FMT = 'YYYY-MM-DD';

/**
 * Apply an input mask progressively. Placeholders: `0`/`9` = digit, `A` = letter,
 * `S` = letter-or-digit; every other char is a literal. Junk the mask can't place is
 * dropped, and trailing literals are only emitted once there's input to follow them, so
 * the field reads naturally while typing.
 */
function applyMask(mask: string, raw: string): string {
    if (!mask) return raw;
    const accepts = (m: string, c: string): boolean =>
        m === '0' || m === '9' ? /[0-9]/.test(c)
            : m === 'A' ? /[a-zA-Z]/.test(c)
            : /[a-zA-Z0-9]/.test(c); // 'S'
    const isPlaceholder = (m: string) => m === '0' || m === '9' || m === 'A' || m === 'S';

    let out = '';
    let ri = 0;
    for (let mi = 0; mi < mask.length; mi++) {
        const m = mask[mi];
        if (isPlaceholder(m)) {
            while (ri < raw.length && !accepts(m, raw[ri])) ri++; // skip chars this slot rejects
            if (ri >= raw.length) break;
            out += raw[ri];
            ri++;
        } else {
            if (ri >= raw.length) break; // don't leave a dangling literal
            out += m;
            if (raw[ri] === m) ri++; // absorb a literal the user typed themselves
        }
    }
    return out;
}

/** Resolve a question's component kind — explicit `type`, else inferred from select/options. */
function coerceKind(q: Record<string, unknown>, select: SelectMode, hasOptions: boolean): QKind {
    const raw = asStr(q.type).trim().toLowerCase();
    const known: QKind[] = ['single', 'multi', 'dropdown', 'date', 'masked', 'text'];
    let kind = (known as string[]).includes(raw) ? (raw as QKind) : (hasOptions ? select : 'text');
    // Choice kinds are meaningless without options; free-input kinds never carry them.
    if (CHOICE_KINDS.includes(kind) && !hasOptions) kind = 'text';
    return kind;
}

/** Coerce an already-parsed value into a normalised Questionnaire, or null if unusable. */
function coerceQuestionnaire(raw: unknown): Questionnaire | null {
    const obj = asObj(raw);
    if (!obj) return null;
    const rawQs = obj.questions;
    if (!Array.isArray(rawQs) || rawQs.length === 0) return null;

    const questions: AskQuestion[] = [];
    for (const item of rawQs) {
        const q = asObj(item);
        if (!q) continue;
        const text = asStr(q.text).trim();
        if (!text) continue;
        const select: SelectMode = asStr(q.select).toLowerCase() === 'multi' ? 'multi' : 'single';
        const options = (Array.isArray(q.options) ? q.options : [])
            .filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
            .map((o) => o.trim());
        const id = asStr(q.id).trim() || undefined;
        const kind = coerceKind(q, select, options.length > 0);
        const mask = kind === 'masked' ? asStr(q.mask).trim() || undefined : undefined;
        questions.push({ id, text, select, options: CHOICE_KINDS.includes(kind) ? options : [], kind, mask });
    }
    if (questions.length === 0) return null;

    const title = asStr(obj.title).trim() || undefined;
    return { title, questions };
}

/** Read the questionnaire from the tool-call args. Handles every shape the args can arrive in:
 *  - a bare JSON string — either the questionnaire itself, or an args object wrapping it
 *    (a single-string-param call can serialise as just the value);
 *  - an args object `{ questionnaire: string | object }`;
 *  - the legacy `{ question }` yes/no shape. */
function readQuestionnaire(args: unknown): Questionnaire | null {
    if (typeof args === 'string' && args.trim()) {
        try {
            const parsedJson = JSON.parse(args);
            const direct = coerceQuestionnaire(parsedJson); // the string WAS the questionnaire
            if (direct) return direct;
            args = parsedJson; // else it's a JSON args object — handle it below
        } catch {
            return null;
        }
    }

    const a = asObj(args);
    if (!a) return null;

    if (typeof a.questionnaire === 'string' && a.questionnaire.trim()) {
        try {
            const parsed = coerceQuestionnaire(JSON.parse(a.questionnaire));
            if (parsed) return parsed;
        } catch {
            /* fall through */
        }
    }
    const fromObj = coerceQuestionnaire(a.questionnaire);
    if (fromObj) return fromObj;

    if (typeof a.question === 'string' && a.question.trim()) {
        return {
            questions: [{ id: 'q0', text: a.question.trim(), select: 'single', options: ['Yes', 'No'], kind: 'single' }],
        };
    }
    return null;
}

/**
 * Deep-parse the tool's echoed answer into QAnswer[]. The response can arrive
 * char-indexed (persisted shape) or JSON-string-encoded one — or even two —
 * levels deep, because the transport re-encodes the tool's already-JSON string
 * result. Returns the answers array, or null if it's a plain sentinel string
 * (timeout / dismissed) or otherwise unparseable.
 */
function parseAnswers(raw: unknown): QAnswer[] | null {
    let v: unknown = raw;
    if (v && typeof v === 'object') {
        const collapsed = InteractionPartUtils.tryCollapseCharIndexedObject(v);
        if (typeof collapsed === 'string' && collapsed.trim()) v = collapsed;
    }
    for (let i = 0; i < 3 && typeof v === 'string'; i++) {
        const t = v.trim();
        if (!(t.startsWith('{') || t.startsWith('[') || t.startsWith('"'))) break;
        try {
            v = JSON.parse(t);
        } catch {
            break;
        }
    }
    const obj = asObj(v);
    return obj && Array.isArray(obj.answers) ? (obj.answers as QAnswer[]) : null;
}

/** A plain sentinel (non-JSON) answer string — e.g. "(the user did not answer in time)". */
function sentinelOf(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t || t.startsWith('{') || t.startsWith('[') || t.startsWith('"')) return null;
    return t;
}

// Only `part` is required; the rest of PartRendererProps is optional so the
// pane-level AskUserSheet can render this with just the parked part + `embedded`.
// Still assignable to the PartRenderer registry, which passes the full props.
type AskUserPartProps = Partial<PartRendererProps> & { part: PartRendererProps['part']; embedded?: boolean };

const AskUserPart: React.FC<AskUserPartProps> = ({ part, embedded = false }) => {
    const { agentGatewayService } = useAiChatServices();
    const [submitted, setSubmitted] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<number, QState>>({});

    const theme = useTheme();

    const callId = part.functionDetails?.id;
    const questionnaire = useMemo(
        () => readQuestionnaire(part.functionDetails?.args),
        [part.functionDetails?.args],
    );

    // TEMP diagnostic (issue #61) — confirm the args shape reaching the client. Remove after.
    // eslint-disable-next-line no-console
    console.debug('[AskUserPart] typeof args:', typeof part.functionDetails?.args,
        'args:', part.functionDetails?.args, '→ parsed?', !!questionnaire);

    // Answered = submitted this session, OR the function response already carries
    // the answer (reload / late render). Keep the response RAW — parseAnswers
    // unwraps whatever encoding it arrives in.
    const serverAnswer = part.eventType === EventType.FunctionResponse
        ? (part.functionDetails?.response ?? null)
        : null;
    const effective: unknown = submitted ?? serverAnswer;
    const locked = effective != null || !callId || !questionnaire;

    if (!questionnaire) return null;
    const { title, questions } = questionnaire;
    const qid = (i: number) => questions[i].id || String(i);

    const outerSx = embedded
        ? { maxWidth: '100%' }
        : { border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5, my: 0.5, maxWidth: '100%' };

    // ── Locked: pure Question -> Answer summary (no interactive controls) ──────
    if (locked) {
        const parsed = parseAnswers(effective);
        const sentinel = parsed ? null : sentinelOf(effective);
        return (
            <Box sx={outerSx}>
                {title && (
                    <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>{title}</Typography>
                )}
                <Stack spacing={1.25}>
                    {questions.map((q, i) => {
                        const a = parsed?.find((x) => x.questionId === qid(i)) ?? null;
                        const sel = a?.selected ?? [];
                        const note = (a?.note ?? '').trim();
                        const primary = sel.length ? sel.join(', ') : (note || '—');
                        const extraNote = sel.length && note ? note : '';
                        const answered = sel.length > 0 || (q.options.length === 0 && !!note);
                        return (
                            <Box key={qid(i)}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{q.text}</Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 0.25, color: answered ? 'text.primary' : 'text.secondary' }}
                                >
                                    {primary}
                                </Typography>
                                {extraNote && (
                                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        “{extraNote}”
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    {sentinel ?? '✓ answered'}
                </Typography>
            </Box>
        );
    }

    // ── Interactive: the live questionnaire form (shown in the sheet) ──────────
    const getState = (i: number): QState => answers[i] ?? { selected: [], note: '' };
    const selectedFor = (i: number): string[] => getState(i).selected;
    const noteFor = (i: number): string => getState(i).note;

    const anyNote = Object.values(answers).some((s) => s.note.trim().length > 0);
    const isShortcut = questions.length === 1 && questions[0].kind === 'single';
    const instantSubmit = isShortcut && !anyNote;
    const showSubmit = !instantSubmit;

    const buildPayload = (state: Record<number, QState>): string =>
        JSON.stringify({
            answers: questions.map((q, i) => {
                const s = state[i] ?? { selected: [], note: '' };
                const note = s.note.trim();
                return { questionId: q.id || String(i), selected: s.selected, ...(note ? { note } : {}) };
            }),
        });

    const submit = (state: Record<number, QState> = answers) => {
        if (!callId) return;
        const payload = buildPayload(state);
        setSubmitted(payload);
        agentGatewayService.answerHumanTool(callId, payload);
    };

    const pickSingle = (i: number, opt: string) => {
        const cur = getState(i);
        const next = cur.selected[0] === opt ? [] : [opt]; // click again to deselect (= none)
        const nextState = { ...answers, [i]: { ...cur, selected: next } };
        if (instantSubmit && next.length > 0) {
            submit(nextState);
            return;
        }
        setAnswers(nextState);
    };

    const toggleMulti = (i: number, opt: string) => {
        const cur = getState(i);
        const next = cur.selected.includes(opt)
            ? cur.selected.filter((o) => o !== opt)
            : [...cur.selected, opt];
        setAnswers({ ...answers, [i]: { ...cur, selected: next } });
    };

    const setNote = (i: number, note: string) => {
        setAnswers({ ...answers, [i]: { ...getState(i), note } });
    };

    // Single-value input (dropdown / date / masked): the value lives in `selected[0]`.
    const setValue = (i: number, value: string) => {
        setAnswers({ ...answers, [i]: { ...getState(i), selected: value ? [value] : [] } });
    };
    const valueFor = (i: number): string => selectedFor(i)[0] ?? '';

    return (
        <Box sx={outerSx}>
            {title && (
                <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>{title}</Typography>
            )}

            <Stack spacing={2}>
                {questions.map((q, i) => (
                    <Box key={qid(i)}>
                        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>{q.text}</Typography>

                        {q.kind === 'multi' && (
                            <FormGroup>
                                {q.options.map((opt) => (
                                    <FormControlLabel
                                        key={opt}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedFor(i).includes(opt)}
                                                onChange={() => toggleMulti(i, opt)}
                                            />
                                        }
                                        label={<Typography variant="body2">{opt}</Typography>}
                                    />
                                ))}
                            </FormGroup>
                        )}

                        {q.kind === 'single' && (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {q.options.map((opt) => (
                                    <Button
                                        key={opt}
                                        size="small"
                                        variant={selectedFor(i).includes(opt) ? 'contained' : 'outlined'}
                                        color="primary"
                                        onClick={() => pickSingle(i, opt)}
                                        sx={{color:theme.palette.text.secondary}}
                                    >
                                        {opt}
                                    </Button>
                                ))}
                            </Stack>
                        )}

                        {q.kind === 'dropdown' && (
                            <TextField
                                select
                                size="small"
                                fullWidth
                                value={valueFor(i)}
                                onChange={(e) => setValue(i, e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>—</em>
                                </MenuItem>
                                {q.options.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        {q.kind === 'date' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={valueFor(i) ? dayjs(valueFor(i)) : null}
                                    onChange={(d) => setValue(i, d && d.isValid() ? d.format(DATE_FMT) : '')}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        )}

                        {q.kind === 'masked' && (
                            <TextField
                                value={valueFor(i)}
                                onChange={(e) => setValue(i, q.mask ? applyMask(q.mask, e.target.value) : e.target.value)}
                                placeholder={q.mask || 'Type your answer…'}
                                size="small"
                                fullWidth
                            />
                        )}

                        {/* Free-text answer for `text`, or an optional note/"something not listed" for choices. */}
                        {(CHOICE_KINDS.includes(q.kind) || q.kind === 'text') && (
                            <TextField
                                value={noteFor(i)}
                                onChange={(e) => setNote(i, e.target.value)}
                                placeholder={
                                    q.kind === 'text'
                                        ? 'Type your answer…'
                                        : 'Add a note, or something not listed (optional)'
                                }
                                size="small"
                                fullWidth
                                multiline
                                maxRows={3}
                                sx={{ mt: q.kind === 'text' ? 0.5 : 1 }}
                            />
                        )}
                    </Box>
                ))}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                {showSubmit ? (
                    <Button size="small" variant="contained" color="primary" onClick={() => submit()}>
                        Submit
                    </Button>
                ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Pick an option to answer
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export default AskUserPart;
